<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

export interface ContextMenuItem {
  key: string
  label: string
  icon?: unknown
  danger?: boolean
  disabled?: boolean
  divided?: boolean
}

interface Props {
  items: ContextMenuItem[]
}

defineProps<Props>()

const emit = defineEmits<{
  select: [key: string]
}>()

interface MenuState {
  visible: boolean
  x: number
  y: number
}

const state = ref<MenuState>({ visible: false, x: 0, y: 0 })
// v3.13.81: 菜单元素引用 — 渲染后量取真实尺寸做视口钳制
const menuEl = ref<HTMLElement | null>(null)

function open(e: MouseEvent, items: ContextMenuItem[]): void {
  if (items.length === 0) return
  // v3.13.73 fix: 左键按钮展开时, 该 click 会继续冒泡到 document 的全局
  // 关闭监听 (onGlobalClick) → 菜单刚开即被同一事件关闭, 表现为"点不开"。
  // 右键路径走 contextmenu 事件不受影响; 这里拦掉冒泡, 由调用方 toggle。
  e.preventDefault()
  e.stopPropagation()
  // v3.13.81: 视口钳制 — 手机上点靠右/靠下的行, 菜单会开出屏幕外
  const W = window.innerWidth
  const H = window.innerHeight
  const estW = 176
  const estH = items.length * 36 + 12
  const x = Math.max(8, Math.min(e.clientX, W - estW - 8))
  const y = Math.max(8, Math.min(e.clientY, H - estH - 8))
  state.value = { visible: true, x, y }
  // 渲染后按真实尺寸二次校正
  void nextTick(() => {
    const el = menuEl.value
    if (!el) return
    const r = el.getBoundingClientRect()
    let nx = state.value.x
    let ny = state.value.y
    if (nx + r.width > W - 8) nx = Math.max(8, W - r.width - 8)
    if (ny + r.height > H - 8) ny = Math.max(8, H - r.height - 8)
    if (nx !== state.value.x || ny !== state.value.y) {
      state.value = { visible: true, x: nx, y: ny }
    }
  })
}

function close(): void {
  state.value.visible = false
}

function pick(item: ContextMenuItem): void {
  if (item.disabled) return
  emit('select', item.key)
  close()
}

function onGlobalClick(): void {
  if (state.value.visible) close()
}

function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') close()
}

onMounted(() => {
  document.addEventListener('click', onGlobalClick)
  document.addEventListener('keydown', onKey)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onGlobalClick)
  document.removeEventListener('keydown', onKey)
})

// Expose for parent usage (isOpen 供左键触发方实现 toggle)
defineExpose({ open, close, isOpen: () => state.value.visible })
</script>

<template>
  <Teleport to="body">
    <transition name="ctx">
      <ul
        ref="menuEl"
        v-if="state.visible"
        class="fixed z-50 min-w-[10rem] rounded-md border border-border bg-bg-surface py-1 shadow-lg"
        :style="{ top: state.y + 'px', left: state.x + 'px' }"
        role="menu"
      >
        <template v-for="item in items" :key="item.key">
          <li v-if="item.divided" class="my-1 border-t border-border" />
          <li>
            <button
              type="button"
              role="menuitem"
              class="flex w-full items-center gap-2 px-3 py-1.5 text-sm transition-colors duration-fast"
              :class="
                item.disabled
                  ? 'cursor-not-allowed text-fg-subtle'
                  : item.danger
                    ? 'text-danger hover:bg-danger/10'
                    : 'text-fg hover:bg-bg-hover'
              "
              :disabled="item.disabled"
              @click="pick(item)"
            >
              <component v-if="item.icon" :is="item.icon" :size="14" />
              <span>{{ item.label }}</span>
            </button>
          </li>
        </template>
      </ul>
    </transition>
  </Teleport>
</template>

<style scoped>
.ctx-enter-active,
.ctx-leave-active {
  transition: opacity 120ms ease, transform 120ms ease;
}
.ctx-enter-from,
.ctx-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
</style>
